import z from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { zodParseWithLog } from "src/api/apiErrorLogs";
import { hydraSearchToAppSearch } from "../../_common/hydra/search";
import { isQueryReady } from "src/api/axiosClient/utils";
import { OlapScoresQuery } from "src/context/OlapScores/useOlapScoresParams";
import { OlapScoresResult, OlapScoresResultSchema } from "../olapScores/olapScoresSchema";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { fetchAllHydraMembers, type HydraCollectionParams } from "src/api/_common/hydra/fetchAllHydraMembers";

type Options = {
    cacheTime?: number;
    // page?: number;
    // itemsPerPage?: number;
    search?: string;
    params?: AxiosParams;
    scoresQuery?: OlapScoresQuery;
    enabled?: boolean;
    fetchAllPages?: boolean;
};

export const packagingGridKey = "allPackagingsGrid" as const;
export const packagingElementsGridKey = "allPackagingElementsGrid" as const;
export const packagingCompositesGridKey = "allPackagingCompositesGrid" as const;

const useGetPackagingCollection = (url: string, queryKeyPrefix: string, options?: Options) => {
    const {
        cacheTime = 0,
        search = "",
        scoresQuery,
        params = {},
        enabled = true,
        fetchAllPages = false,
    } = options || {};
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const campaignId = params.campaign ?? currentCampaign?.id;
    const isEnabled = enabled && organizationId !== undefined;

    const paramsKey = JSON.stringify(params);
    const query = useQuery({
        queryKey: [
            queryKeyPrefix,
            organizationId ?? null,
            isEnabled,
            search,
            scoresQuery,
            paramsKey,
            campaignId ?? null,
            fetchAllPages,
        ],
        staleTime: 0,
        cacheTime: cacheTime,
        enabled: isEnabled,
        keepPreviousData: false,
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load packagings while disabled or without a selected organization");
            }

            const requestParams = {
                q: search !== "" ? search : undefined,
                ...scoresQuery,
                ...params,
                campaign: campaignId,
                organization_id: organizationId,
            };
            if (fetchAllPages) {
                const paginationParams = Object.fromEntries(
                    Object.entries(requestParams).filter(([key]) => key !== "itemsPerPage" && key !== "page"),
                );
                const hasUnsupportedParameter = Object.values(paginationParams).some(
                    (value) =>
                        value !== undefined &&
                        typeof value !== "boolean" &&
                        typeof value !== "number" &&
                        typeof value !== "string",
                );
                if (hasUnsupportedParameter) {
                    throw new Error("Cannot load all packaging pages with non-scalar collection parameters");
                }

                const packaging = await fetchAllHydraMembers({
                    client: loggedInApiCall,
                    endpoint: url,
                    itemsPerPage: 100,
                    params: paginationParams as HydraCollectionParams,
                });
                const data = zodParseWithLog(packaging, PackagingListSchema.array(), "allPackagings").map(
                    normalizePackagingListType,
                );
                return {
                    packagings: data,
                    totalItems: data.length,
                    appSearch: {},
                };
            }

            const request = await loggedInApiCall.get(url, { params: requestParams });
            const totalItems = request.data["hydra:totalItems"];
            const packaging = request.data["hydra:member"];
            const appSearch = hydraSearchToAppSearch(request.data["hydra:search"]);
            const data = zodParseWithLog(packaging, PackagingListSchema.array(), "allPackagings").map(
                normalizePackagingListType,
            );
            return {
                packagings: data,
                totalItems: totalItems,
                appSearch,
            };
        },
        onError: () => {
            return {
                packagings: [],
                totalItems: 0,
                appSearch: {},
            };
        },
    });

    const data = isEnabled ? query.data : undefined;
    const isReady = isQueryReady(query, isEnabled);
    return {
        ...data,
        isReady,
        ...query,
        data,
    };
};

export const useGetAllPackaging = (options?: Options) => {
    return useGetPackagingCollection("/api/packagings", packagingGridKey, options);
};

export const useGetAllPackagingElements = (options?: Options) => {
    return useGetPackagingCollection("/api/packaging-elements", packagingElementsGridKey, options);
};

export const useGetAllPackagingComposites = (options?: Options) => {
    return useGetPackagingCollection("/api/packaging-composites", packagingCompositesGridKey, options);
};

export type PackagingList = {
    "@id": IRI;
    "@type"?: "PackagingElement" | "PackagingComposite";
    type?: "element" | "composite";
    id: number;
    name: string;
    externalId?: string;
    weight?: number | null;
    canView: boolean;
    canModify: boolean;
    canDelete: boolean;
    olapScores: OlapScoresResult;
    tags?: Record<string, string[]>; // Format objet groupé par type de tag
};

const PackagingListSchema: z.ZodSchema<PackagingList> = z.object({
    "@id": z.string(),
    "@type": z.enum(["PackagingElement", "PackagingComposite"]).optional(),
    type: z.enum(["element", "composite"]).optional(),
    id: z.number(),
    name: z.string(),
    externalId: z.string().optional(),
    weight: z.number().nullable().optional(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
    olapScores: OlapScoresResultSchema,
    tags: z.any(),
});

const normalizePackagingListType = (packaging: PackagingList): PackagingList => {
    if (packaging["@type"] !== undefined) {
        return packaging;
    }

    return {
        ...packaging,
        "@type": packaging.type === "composite" ? "PackagingComposite" : "PackagingElement",
    };
};
